import * as z from 'zod';
import { MAX_NOTES_LENGTH } from '../limits.js';
import { isoDateTimeSchema, shortRefSchema, strict, uuidSchema } from './common.js';
import { paginationQuerySchema } from './pagination.js';
import { presenceStateSchema } from './person.js';
import { directionSchema, presenceMethodSchema, presenceReasonCodeSchema, presenceResultSchema, } from './presence.js';
/**
 * An incident: a movement that was not ordinary, or one somebody wrote about.
 *
 * **This is a separate shape from {@link presenceEventSchema} and must stay one.** A presence event
 * is what the gate's own submit response, the anomalies window and the movement log all carry, and
 * every one of those is reachable by an operator or by an API key holding `presence:read`. An
 * incident carries two things none of them may: the person's NAME, and the operator's decrypted
 * PROSE about them. Widening the event shape to hold either would put a decrypted note on the gate's
 * hot path and in a window a machine can read. So there are two shapes, and the serialiser for each
 * is total over its input.
 *
 * **What makes a row an incident is one sentence, and it is deliberately not "the result was bad".**
 * The predicate is `result <> 'accepted' OR reason_note_enc IS NOT NULL`, and the second half is
 * load-bearing. An operator's note survives validation only on an override outcome, so the first
 * half alone would cover everything a human at a door can write — but a bulk correction's audience
 * is `system`, which keeps its reason and note on every row it writes, and almost all of those rows
 * are `accepted` because a correction moves somebody in the direction that changes their state. With
 * the first half alone, the reason an administrator gave for emptying the building would stay
 * exactly as unreadable as it is today, on the one screen built to make notes readable. Written this
 * way, "no stored note is unreachable" is true by construction rather than by inspection.
 */
/**
 * What the incident log narrows on.
 *
 * `result` is the FULL {@link presenceResultSchema} rather than a narrower "outcome" enum, because
 * the predicate above already decides which rows exist and this only narrows within them. That makes
 * `result=accepted` a legal and useful query — it is "show me the bulk-correction rows that carry a
 * note" — where a three-member enum would have made those rows visible and unfilterable.
 *
 * There is deliberately no filter on the note's TEXT: it is ciphertext, and a filter over one would
 * have to decrypt the table.
 */
export const incidentQuerySchema = paginationQuerySchema.omit({ direction: true }).extend({
    personId: uuidSchema.optional(),
    result: presenceResultSchema.optional(),
    /** The MOVEMENT's direction, as everywhere else in this domain; never a sort order. */
    direction: directionSchema.optional(),
    method: presenceMethodSchema.optional(),
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
/**
 * Why a note is not on screen, as a closed set rather than as a null.
 *
 * **Four members, and the last two are the reason this is not a boolean.** `erased` means the
 * person's data key is gone, so the note is unopenable for ever and that is a fact about a
 * deliberate, irreversible act. `unreadable` means the ciphertext is there and this process could
 * not open it — a rotated or damaged keyring — which is an infrastructure fault somebody must be
 * told about. Collapsing them into one null would make the screen tell an administrator that a note
 * was destroyed on purpose when the truth is that something is broken, which is a false statement
 * about an irreversible act and a keyring problem rendered invisible in the same stroke.
 */
export const noteStateSchema = z.enum(['none', 'readable', 'erased', 'unreadable']);
/**
 * One incident, decrypted for a reader holding `presence:investigate`.
 *
 * `name` is unbounded, exactly as `rollCallEntrySchema.name` and `personSummarySchema.displayName`
 * are: a display name is built from person fields and no bound here would be provably unreachable,
 * and a `.max()` the server can exceed is a blank panel rather than a refused request. `reasonNote`
 * IS bounded, because that bound is enforced at write time by the three request schemas that accept
 * one, so the server cannot exceed it.
 */
export const incidentSchema = strict({
    id: uuidSchema,
    occurredAt: isoDateTimeSchema,
    personId: uuidSchema,
    shortRef: shortRefSchema,
    /** `null` when the projection cannot name them — erased, or still building. The row stays. */
    name: z.string().nullable(),
    direction: directionSchema,
    result: presenceResultSchema,
    method: presenceMethodSchema,
    reasonCode: presenceReasonCodeSchema.nullable(),
    noteState: noteStateSchema,
    /** The prose, or `null`. Non-null exactly when `noteState` is `readable`. */
    reasonNote: z.string().max(MAX_NOTES_LENGTH).nullable(),
    operatorId: uuidSchema.nullable(),
    clientLabel: z.string().nullable(),
    previousState: presenceStateSchema,
    newState: presenceStateSchema,
});
//# sourceMappingURL=incident.js.map